import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import {
  ImageSubsection,
  ImageSubsectionWithLink,
  SubsectionProps,
  SubsectionWithLink,
  SubsectionWithNoLink,
} from '@widgets/SectionNavigation/types'
import contextSectionNavigation from '@contexts/sectionNavigation'
import Blink from '@components/Blink'
import ImageSubsectionComponent from './ImageSubsection'
import useSectionTracking from '@hooks/useSectionTracking'

const { SectionNavigationContext } = contextSectionNavigation

interface StyledSubsectionProps {
  isVideo: boolean
}

const subsectionStyle = (isVideo: boolean) => css`
  ${({
    theme: {
      fonts: { goodOT: goodOTFont },
      colors: {
        blicktvGrey: { btvGrey_60 },
        white,
      },
    },
  }) => {
    return `
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${goodOTFont};
  font-size: 17px;
  font-weight: 500;
  line-height: 18px;
  color: ${isVideo ? white : btvGrey_60};
  margin: 0 10px;
`
  }}
`

const StyledSubsection = styled(Blink)<StyledSubsectionProps>`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
    },
    isVideo,
  }) => {
    return css`
      ${subsectionStyle(isVideo)}
      text-decoration: none;
      max-width: 200px;
      &:hover {
        color: ${brand_55};
      }
    `
  }}
`

const StyledSubsectionNoLink = styled.div<StyledSubsectionProps>`
  ${({ isVideo }) => {
    return css`
      ${subsectionStyle(isVideo)}
    `
  }}
`

const StyledLink = styled(Blink)`
  text-decoration: none;
`

const isImageSubsection = (subsection: any): subsection is ImageSubsection =>
  !!subsection.imageUrl

const isImageSubsectionWithLink = (
  subsection: ImageSubsection
): subsection is ImageSubsectionWithLink => !!subsection.link?.href

const isNoLinkSubsection = (
  subsection: SubsectionWithNoLink | SubsectionWithLink
): subsection is SubsectionWithNoLink =>
  !subsection?.link || Object.keys(subsection.link).length === 0

const Subsection: FunctionComponent<SubsectionProps> = ({ children }) => {
  const { isVideoBlock } = useContext(SectionNavigationContext)
  const { name } = children

  const sectionTracking = useSectionTracking(name)

  if (isImageSubsection(children)) {
    if (isImageSubsectionWithLink(children)) {
      return (
        <StyledLink
          {...children.link}
          ariaLabel={children.name}
          onClick={sectionTracking.onClick}>
          <ImageSubsectionComponent>{children}</ImageSubsectionComponent>
        </StyledLink>
      )
    }
    return <ImageSubsectionComponent>{children}</ImageSubsectionComponent>
  }

  if (isNoLinkSubsection(children)) {
    return (
      <StyledSubsectionNoLink isVideo={isVideoBlock}>
        {children.name}
      </StyledSubsectionNoLink>
    )
  }

  const { link } = children
  return (
    <StyledSubsection
      {...link}
      onClick={sectionTracking.onClick}
      ariaLabel={name}
      isVideo={isVideoBlock}>
      {name}
    </StyledSubsection>
  )
}

export default Subsection
